import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { ShareIcon, DownloadIcon, PrinterIcon } from "@heroicons/react/outline";
import { POCService, pocDataMap } from "../../../services";
import { Skeleton } from "../../../components/ui/skeleton";
import { QRCodeCanvas } from "qrcode.react";
import { GoLocation } from "react-icons/go";
import { createItemStore } from "../../../stores/createItemStore";

// ========================================================
// Step 1: Zustand store instantiation
// ========================================================
const storeName = "pocs";
const useItems = createItemStore(
  storeName,
  (poc) => poc.POCId,
  (args) => POCService.getPOCDetails(args[0]),
  pocDataMap
);
// ========================================================

const ViewPOCPage = () => {
  const navigate = useNavigate();
  const { POCID } = useParams();
  const qrCanvasRef = useRef(null);
  // const gurl = "https://maps.app.goo.gl/eFEMEZSUtFRg4VgS8";

  const [activeTab, setActiveTab] = useState("general");

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };

  const handleEdit = (POCId) => {
    console.log("the id: ", POCId);

    navigate(`/poc/edit/${POCId}`);
  };

  const handleShare = () => {
    console.log("Share clicked");
    const canvas = qrCanvasRef.current;
    if (canvas) {
      canvas.toBlob(async (blob) => {
        const file = new File([blob], "qr-code.png", { type: "image/png" });
        if (navigator.share) {
          try {
            await navigator.share({
              title: "QR Code",
              text: "Scan this QR code",
              files: [file],
            });
          } catch (err) {
            console.error("Error sharing", err);
          }
        } else {
          alert("Sharing is not supported in your browser.");
        }
      });
    }
  };

  const handleDownload = () => {
    const canvas = qrCanvasRef.current;
    if (canvas) {
      const url = canvas.toDataURL("image/png"); // this is just the QR code image
      const link = document.createElement("a");
      link.href = url;
      link.download = "qr-code.png";
      link.click();
    }
  };

  const handlePrint = () => {
    console.log("Print clicked");
    const canvas = qrCanvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL("image/png");
      const windowContent = `
      <html>
        <head><title>Print QR Code</title></head>
        <body>
          <img src="${dataUrl}" style="width:180px;" />
          <script>
            window.onload = function () {
              window.print();
              window.onafterprint = function () { window.close(); };
            }
          </script>
        </body>
      </html>
    `;
      const printWindow = window.open("", "_blank");
      printWindow.document.write(windowContent);
      printWindow.document.close();
    }
  };

  const extractCoordinatesFromUrl = (url) => {
    if (!url) return null;

    // Match the pattern for coordinates in the URL
    const match = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (match && match.length >= 3) {
      return {
        latitude: parseFloat(match[1]),
        longitude: parseFloat(match[2]),
      };
    }
    return null;
  };

  // ========================================================
  // Step 2: Zustand store and page hydration
  // ========================================================
  const { fetchItems, getSortedFiltered } = useItems((state) => state);
  const data = getSortedFiltered((poc) => poc.POCId === POCID);
  const poc = data?.[0] ?? null;
  useEffect(() => {
    fetchItems({}, [POCID]);
  }, [POCID]);
  // ========================================================

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        <div className="p-8 flex flex-col">
          <div className="bg-white shadow p-4 pb-1 rounded-lg">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <div className="bg-lime-400 p-3 sm:p-4 rounded-full">
                  <GoLocation className="w-10 h-10 sm:w-16 sm:h-16 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-[#111827]">
                    {poc ? poc.name : <Skeleton className="w-32 h-4" />}
                  </h1>
                  <div className="mt-6 flex space-x-8">
                    <div>
                      <div className="text-sm font-semibold text-[#6B7280]">
                        Date Created
                      </div>
                      <div className="text-sm text-[#111827]">
                        {poc ? (
                          new Date(poc.createDate).toLocaleString()
                        ) : (
                          <Skeleton className="w-32 h-4" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-4">
                <Button onClick={() => handleEdit(POCID)}>Edit</Button>
              </div>
            </div>
          </div>
          {/* Tabs Section */}
          <div className="flex">
            <button
              onClick={() => handleTabSwitch("general")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "general"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              General Info
            </button>
          </div>

          {/* Details Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 p-4 sm:p-6 mt-0 bg-white shadow rounded-r-lg rounded-b-lg ">
            {/* POC Name */}
            <div>
              <Label>POC Name</Label>
              <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                {poc ? poc.name : <Skeleton className="w-32 h-4" />}
              </p>
            </div>

            {/* QR Code with Share, Download, Print buttons */}
            <div className="flex flex-col gap-4">
              <Label>POC QR Code</Label>
              <div className="flex flex-col sm:flex-row sm:items-start gap-4 border border-gray-300 rounded p-4">
                {poc?.qrLink ? (
                  <QRCodeCanvas
                    value={poc.qrLink}
                    size={180}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    ref={qrCanvasRef}
                  />
                ) : (
                  <Skeleton className="w-48 h-48" />
                )}

                <div className="flex flex-col gap-2 sm:pl-6 w-full sm:w-auto">
                  <Button variant={"hollowGreen"} onClick={handleShare}>
                    <ShareIcon className="h-5 w-5 mr-2" />
                    Share
                  </Button>
                  <Button variant={"hollowGreen"} onClick={handleDownload}>
                    <DownloadIcon className="h-5 w-5 mr-2" />
                    Download
                  </Button>
                  <Button variant={"hollowGreen"} onClick={handlePrint}>
                    <PrinterIcon className="h-5 w-5 mr-2" />
                    Print
                  </Button>
                </div>
              </div>
            </div>

            {/* Address 1 */}
            <div>
              <Label>Address 1</Label>
              <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                {poc ? poc.addressLine1 : <Skeleton className="w-32 h-4" />}
              </p>
            </div>

            {/* City */}
            <div>
              <Label>City</Label>
              <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                {poc ? poc.city : <Skeleton className="w-32 h-4" />}
              </p>
            </div>

            {/* Address 2 */}
            <div>
              <Label>Address 2</Label>
              <p className="w-full p-2 border-b-2 border-gray-300 text-sm mt-2">
                {poc ? poc.addressLine2 : <Skeleton className="w-32 h-4" />}
              </p>
            </div>

            {/* State */}
            <div>
              <Label>State</Label>
              <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                {poc ? poc.state : <Skeleton className="w-32 h-4" />}
              </p>
            </div>

            {/* Postcode */}
            <div>
              <Label>Postcode</Label>
              <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                {poc ? poc.postcode : <Skeleton className="w-32 h-4" />}
              </p>
            </div>

            {/* Country */}
            <div>
              <Label>Country</Label>
              <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                {poc ? poc.country : <Skeleton className="w-32 h-4" />}
              </p>
            </div>

            {/* Google map link */}
            <div className="">
              {" "}
              {/* Changed to col-span-2 to make it full width */}
              <div className="col-span-2">
                <Label>Map Location</Label>
                {poc ? (
                  poc.map ? (
                    <div className="mt-2">
                      <a
                        href={poc.map}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Open in Google Maps
                      </a>
                      <div className="mt-2 h-64 w-full rounded-lg overflow-hidden border border-gray-300">
                        <iframe
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          style={{ border: 0 }}
                          src={`https://maps.google.com/maps?q=${
                            extractCoordinatesFromUrl(poc.map)?.latitude
                          },${
                            extractCoordinatesFromUrl(poc.map)?.longitude
                          }&z=15&output=embed`}
                          allowFullScreen
                        ></iframe>
                      </div>
                    </div>
                  ) : (
                    <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                      No map location available
                    </p>
                  )
                ) : (
                  <Skeleton className="w-full h-64" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPOCPage;
